import React from 'react';
import { Download, ExternalLink } from 'lucide-react';
import pressData from '../data/press.json';
import PageHero from '../components/PageHero';

const Press = () => {
  return (
    <div className="relative">
      <PageHero 
        title={pressData.pageTitle}
        subtitle={pressData.pageSubtitle}
      />

      <section className="section-padding pt-0">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Brand Assets */}
            <div data-testid="brand-assets">
              <h2 className="text-2xl font-bold text-white mb-4">
                {pressData.brandAssets.title}
              </h2>
              <p className="text-slate-400 mb-8">
                {pressData.brandAssets.description}
              </p>

              <div className="space-y-4">
                {pressData.brandAssets.assets.map((asset, index) => (
                  <div 
                    key={index}
                    className="glass-panel p-6 flex items-center justify-between gap-4"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-xl bg-violet-500/10 flex items-center justify-center overflow-hidden">
                        <img 
                          src={asset.url} 
                          alt={asset.name}
                          className="w-12 h-12 object-contain"
                        />
                      </div>
                      <div>
                        <h3 className="text-white font-medium">{asset.name}</h3>
                        <p className="text-sm text-slate-500">{asset.format} • {asset.description}</p>
                      </div>
                    </div>
                    <a
                      href={asset.url}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-3 rounded-xl bg-violet-500/10 text-violet-400 hover:bg-violet-500/20 transition-colors"
                      data-testid={`download-asset-${index}`}
                      aria-label={`Download ${asset.name}`}
                    >
                      <Download size={20} />
                    </a>
                  </div>
                ))}
              </div>
            </div>

            {/* Brand Guidelines */}
            <div data-testid="brand-guidelines">
              <h2 className="text-2xl font-bold text-white mb-4">
                {pressData.brandGuidelines.title}
              </h2>
              
              <div className="glass-panel p-6 mb-8">
                <ul className="space-y-3">
                  {pressData.brandGuidelines.rules.map((rule, index) => (
                    <li key={index} className="flex items-start gap-3 text-slate-400">
                      <span className="w-1.5 h-1.5 rounded-full bg-violet-500 mt-2 shrink-0" />
                      {rule}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Color Swatches */}
              <div className="glass-panel p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Brand Colors</h3>
                <div className="flex gap-4">
                  <div className="text-center">
                    <div 
                      className="w-16 h-16 rounded-xl mb-2"
                      style={{ backgroundColor: pressData.brandGuidelines.colors.primary }}
                    />
                    <span className="text-xs text-slate-500">Primary</span>
                    <p className="text-xs text-slate-400 font-mono">{pressData.brandGuidelines.colors.primary}</p>
                  </div>
                  <div className="text-center">
                    <div 
                      className="w-16 h-16 rounded-xl mb-2"
                      style={{ backgroundColor: pressData.brandGuidelines.colors.secondary }}
                    />
                    <span className="text-xs text-slate-500">Secondary</span>
                    <p className="text-xs text-slate-400 font-mono">{pressData.brandGuidelines.colors.secondary}</p>
                  </div>
                  <div className="text-center">
                    <div 
                      className="w-16 h-16 rounded-xl mb-2 border border-white/10"
                      style={{ backgroundColor: pressData.brandGuidelines.colors.background }}
                    />
                    <span className="text-xs text-slate-500">Background</span>
                    <p className="text-xs text-slate-400 font-mono">{pressData.brandGuidelines.colors.background}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Press Contact */}
      <section className="section-padding cosmic-bg">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            <div data-testid="press-contact">
              <h2 className="text-2xl font-bold text-white mb-4">
                {pressData.pressContact.title}
              </h2>
              <p className="text-slate-400 mb-6">
                {pressData.pressContact.description}
              </p>
              <a 
                href={`mailto:${pressData.pressContact.email}`}
                className="inline-flex items-center gap-2 text-violet-400 hover:text-violet-300 transition-colors"
              >
                {pressData.pressContact.email}
                <ExternalLink size={16} />
              </a>
            </div>

            <div data-testid="boilerplate">
              <h2 className="text-2xl font-bold text-white mb-4">
                {pressData.boilerplate.title}
              </h2>
              <div className="glass-panel p-6">
                <p className="text-slate-400 leading-relaxed">
                  {pressData.boilerplate.content}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Press;
